import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Building2, ArrowRight, ExternalLink, Sparkles } from "lucide-react";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const bp = user?.business_profile || {};
  const [form, setForm] = useState({
    name: bp.name || "",
    category: bp.category || "Local Business",
    location: bp.location || "Your City",
    google_place_id: bp.google_place_id || "",
    website_url: bp.website_url || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Business name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/profile", {
        ...form,
        google_place_id: form.google_place_id || null,
        website_url: form.website_url || null,
      });
      setUser(data);
      toast.success("Business set up!");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen text-white">
      <header className="max-w-3xl mx-auto px-6 pt-8">
        <Logo />
      </header>

      <section className="max-w-xl mx-auto px-6 pt-10 pb-20">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-white/5 border border-white/10 text-xs text-zinc-300 mb-4">
            <Sparkles size={12} className="text-[#FF2D75]" /> One quick step
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Tell us about your <span className="gradient-text">business</span>
          </h1>
          <p className="text-sm text-zinc-400">We'll create your QR code and review page right after.</p>
        </div>

        <form onSubmit={save} className="glass p-7 space-y-5" data-testid="onboarding-form">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF2D75] to-[#FF0055] grid place-items-center glow-pink">
            <Building2 size={20} />
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Business name *</label>
            <input
              required
              className="input-dark"
              placeholder="Demo Cafe"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="onb-name-input"
            />
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Google Place ID</label>
            <input
              className="input-dark"
              placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
              value={form.google_place_id}
              onChange={(e) => setForm({ ...form, google_place_id: e.target.value })}
              data-testid="onb-place-id-input"
            />
            <a
              href="https://developers.google.com/maps/documentation/places/web-service/place-id"
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-[#FF2D75] hover:underline inline-flex items-center gap-1 mt-1.5"
            >
              How to find your Place ID <ExternalLink size={10} />
            </a>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Website URL <span className="text-zinc-600">(optional)</span></label>
            <input
              type="url"
              className="input-dark"
              placeholder="https://yourbusiness.com"
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              data-testid="onb-website-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1.5">Shown to customers as a "Visit Website" button after they leave a review.</p>
          </div>

          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="onb-save-btn">
            {busy ? "Saving…" : <>Continue to dashboard <ArrowRight size={16} /></>}
          </button>
          <button
            type="button"
            onClick={() => nav("/app")}
            className="text-xs text-zinc-500 hover:text-white w-full text-center"
            data-testid="onb-skip-btn"
          >
            Skip for now
          </button>
        </form>
      </section>
    </div>
  );
}
